import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, ArrowUpRight } from 'lucide-react';
import { formatCurrency } from '../../utils/formatters';
import { Client } from '../../types';

interface ReceivablesDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  clients: Client[];
}

const ReceivablesDetailModal: React.FC<ReceivablesDetailModalProps> = ({
  isOpen,
  onClose,
  clients
}) => {
  // Filter clients with positive balance
  const clientsWithBalance = clients.filter(client => client.balance > 0);
  
  // Sort by balance (highest first)
  const sortedClients = [...clientsWithBalance].sort((a, b) => b.balance - a.balance);
  
  // Calculate total receivables
  const totalReceivables = clientsWithBalance.reduce((sum, client) => sum + client.balance, 0);
  
  // Calculate percentages
  const getPercentage = (value: number) => {
    if (totalReceivables === 0) return 0;
    return Math.round((value / totalReceivables) * 100);
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50" />
        <Dialog.Content className="fixed left-[50%] top-[50%] max-h-[85vh] w-[90vw] max-w-[600px] translate-x-[-50%] translate-y-[-50%] rounded-lg bg-white dark:bg-gray-800 p-6 shadow-lg overflow-y-auto">
          <Dialog.Title className="text-xl font-semibold mb-4 text-gray-900 dark:text-white flex items-center">
            <ArrowUpRight className="mr-2 h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            Detalle de Cuentas por Cobrar
          </Dialog.Title>
          
          <div className="space-y-6">
            {/* Total Receivables */}
            <div className="bg-emerald-50 dark:bg-emerald-900/30 p-4 rounded-lg">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm font-medium text-emerald-700 dark:text-emerald-300">Total por Cobrar</p>
                  <p className="text-2xl font-bold text-emerald-800 dark:text-emerald-200">{formatCurrency(totalReceivables)}</p>
                </div>
                <div className="p-3 bg-emerald-100 dark:bg-emerald-800 rounded-full">
                  <ArrowUpRight className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
                </div>
              </div>
            </div>
            
            {/* Client List */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Clientes con Saldo Pendiente</h3>
              
              {sortedClients.length > 0 ? (
                <div className="space-y-3">
                  {sortedClients.map((client) => (
                    <div key={client.id} className="bg-white dark:bg-gray-700 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{client.name}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{client.email || 'Sin email'}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-semibold text-emerald-600 dark:text-emerald-400">{formatCurrency(client.balance)}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{getPercentage(client.balance)}% del total</p>
                        </div>
                      </div>
                      <div className="mt-2 w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                        <div 
                          className="bg-emerald-600 dark:bg-emerald-500 h-2 rounded-full" 
                          style={{ width: `${getPercentage(client.balance)}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  No hay cuentas por cobrar
                </div>
              )}
            </div>
          </div>
          
          <div className="mt-6 flex justify-end">
            <button
              onClick={() => onClose()}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
            >
              Cerrar
            </button>
          </div>
          
          <Dialog.Close asChild>
            <button
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              aria-label="Close"
            >
              <X className="h-6 w-6" />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default ReceivablesDetailModal;